import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4PluginsForceDirected from '@amcharts/amcharts4/plugins/forceDirected';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { LoadingSpinner } from '../../..';

// import { Legend } from './Legend';

const nodeLabelName = '{name}';
const nodeLabelEmpty = '';

/**
 * @typedef {DirectedTreeEarningsPerTrackProps}
 * @property {string} chartDivId a unique ID for this chart instance
 * @property {ProDirectedTreeNode[]} data the array of nested ProDirectedTreeNode elements
 * @property {number} height height for the chart div
 */
interface DirectedTreeEarningsPerTrackProps {
    chartDivId: string;
    data: ProDirectedTreeNode[];
    height: number;
}

interface DirectedTreeEarningsPerTrackState {
    chartIsReady: boolean;
}

export class DirectedTreeEarningsPerTrack extends Component<
    DirectedTreeEarningsPerTrackProps,
    DirectedTreeEarningsPerTrackState
> {
    private chart: am4PluginsForceDirected.ForceDirectedTree | null = null;

    // There is a race condition in amCharts which can cause issues if we use a chart div ID rather
    // than a ref. The chartDivId prop is only a fallback for type safety; the ref
    // should be available by the time componentDidMount is called.
    private divRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: DirectedTreeEarningsPerTrackProps) {
        super(props);

        this.chart = null;
        this.buildChart = this.buildChart.bind(this);
        this.divRef = createRef();
        this.state = {
            chartIsReady: false,
        };
    }

    componentDidMount(): void {
        this.buildChart();
    }

    componentDidUpdate(prevProps: DirectedTreeEarningsPerTrackProps): void {
        const {
            props: { data },
        } = this;

        if (!_isEqual(prevProps.data, data)) {
            this.buildChart();
        }
    }

    componentWillUnmount(): void {
        const { chart } = this;

        if (chart) {
            chart.dispose();
        }
    }

    buildChart(): void {
        this.setState({
            chartIsReady: false,
        });

        am4core.createDeferred(
            (): am4core.Sprite => {
                const { chartDivId, data } = this.props;

                const chart = am4core.create(
                    this.divRef?.current ?? chartDivId,
                    am4PluginsForceDirected.ForceDirectedTree,
                );
                const networkSeries = chart.series.push(
                    new am4PluginsForceDirected.ForceDirectedSeries(),
                );

                networkSeries.data = data;
                chart.numberFormatter.numberFormat = '#,###.00';

                networkSeries.dataFields.children = 'children';
                networkSeries.dataFields.name = 'entity_name';
                networkSeries.dataFields.value = 'earnings';
                networkSeries.dataFields.collapsed = 'collapsed';
                networkSeries.dataFields.fixed = 'fixed';

                // Show label only for first two levels
                networkSeries.nodes.template.label.adapter.add(
                    /* eslint-disable indent */
                    'text',
                    (text, target) => {
                        const level = _get(target, ['dataItem', 'level']);

                        if (level !== undefined) {
                            switch (level) {
                                case 0:
                                    return nodeLabelName;
                                case 1:
                                    return nodeLabelName;
                                default:
                                    return nodeLabelEmpty;
                            }
                        }

                        return nodeLabelEmpty;
                    },
                    /* eslint-enable indent */
                );

                networkSeries.nodes.template.propertyFields.x = 'x';
                networkSeries.nodes.template.propertyFields.y = 'y';
                networkSeries.nodes.template.label.text = nodeLabelName;
                networkSeries.nodes.template.label.hideOversized = true;
                networkSeries.nodes.template.label.wrap = true;
                networkSeries.nodes.template.label.dy = 20;
                networkSeries.nodes.template.label.fontSize = 12;
                networkSeries.nodes.template.tooltipText =
                    // eslint-disable-next-line no-template-curly-in-string
                    '{name}: [bold]${value}[/]';
                networkSeries.nodes.template.fontSize = 10;
                networkSeries.fontSize = 10;
                networkSeries.minRadius = am4core.percent(1.5);
                networkSeries.maxRadius = am4core.percent(13);

                const icon = networkSeries.nodes.template.createChild(
                    am4core.Image,
                );

                // Each node will get the path to its SVG file from its 'href' field in the data.
                // See: packages/utils/src/charts/addDirectedTreeIcons.ts
                icon.propertyFields.href = 'href';
                icon.horizontalCenter = 'middle';
                icon.verticalCenter = 'middle';
                icon.width = 20;
                icon.height = 20;

                this.chart = chart;

                return chart;
            },
            this,
            (): void => {
                this.setState({
                    chartIsReady: true,
                });
            },
        );
    }

    render(): JSX.Element {
        const {
            state: { chartIsReady },
            props: { chartDivId, height },
        } = this;

        return (
            <div
                id={chartDivId}
                ref={this.divRef}
                style={{
                    height,
                }}
                data-cy="directed-tree-earnings-per-track-chart"
            >
                {!chartIsReady && (
                    <div className="w-full h-full flex justify-center items-center">
                        <LoadingSpinner />
                    </div>
                )}
            </div>
        );

        // To include the legend, use this version of render logic. The legend is a work in progress.

        // const { chart, networkSeries } = this;

        // return (
        //     <div className="flex text-sm">
        //         <div
        //             id={chartDivId}
        //             className="w-4/5"
        //             style={{
        //                 height: 500,
        //             }}
        //         />
        //         <Legend chart={chart} networkSeries={networkSeries} />
        //     </div>
        // );
    }
}
